import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileStack, Loader2, Plus, Send, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { usePermissions } from "@/lib/permissions";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const ALL = "__all__";
const DISCIPLINES = ["Architectural", "Structural", "MEP", "Electrical", "Plumbing", "HVAC", "Civil", "Interior", "Landscape", "Other"];

function Tile({ label, value, testId }) {
  return <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-bold tabular-nums">{value}</p></div>;
}

/** The drawing register: number, current revision, status and issue date, with every revision kept. */
export default function Drawings() {
  const { projectId } = useProjectCtx();
  const { can } = usePermissions();
  const canCreate = can("DRAWING_REGISTER", "create");
  const canEdit = can("DRAWING_REGISTER", "edit");
  const canApprove = can("DRAWING_REGISTER", "approve");
  const base = `/projects/${projectId}/drawings`;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ status: ALL, discipline: ALL, q: "" });
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([api.get(base), api.get(`${base}/summary`)]);
      setRows(list); setSummary(sum);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const t = filters.q.trim().toLowerCase();
    return rows.filter((r) => (filters.status === ALL || r.status === filters.status) && (filters.discipline === ALL || r.discipline === filters.discipline)
      && (!t || r.number.toLowerCase().includes(t) || r.title.toLowerCase().includes(t)));
  }, [rows, filters]);
  const disciplines = useMemo(() => [...new Set(rows.map((r) => r.discipline).filter(Boolean))], [rows]);

  return (
    <div className="space-y-4" data-testid="drawings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Every drawing with its number, current revision, status and issue date. A new revision supersedes the old one, and the history is kept.</p>
        {canCreate && <Button onClick={() => setAdding(true)} data-testid="drawing-new"><Plus className="h-4 w-4" /> Register a drawing</Button>}
      </div>
      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="drawing-summary">
          <Tile label="Drawings" value={summary.total} testId="dwg-sum-total" />
          <Tile label="Draft" value={summary.draft} testId="dwg-sum-draft" />
          <Tile label="Waiting for approval" value={summary.for_approval} testId="dwg-sum-approval" />
          <Tile label="Approved" value={summary.approved} testId="dwg-sum-approved" />
          <Tile label="Issued" value={summary.issued} testId="dwg-sum-issued" />
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Search</Label><Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Number or title" className="w-56" data-testid="drawing-search" /></div>
        <div className="space-y-1"><Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-44" data-testid="drawing-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>Any status</SelectItem>{["DRAFT", "FOR_APPROVAL", "APPROVED", "ISSUED", "SUPERSEDED", "VOID"].map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ").toLowerCase()}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-xs">Discipline</Label>
          <Select value={filters.discipline} onValueChange={(v) => setFilters((f) => ({ ...f, discipline: v }))}>
            <SelectTrigger className="w-44" data-testid="drawing-filter-discipline"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All</SelectItem>{disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      {!loading && shown.length === 0 ? (
        <EmptyState title={rows.length ? "No drawings match" : "No drawings registered yet"} description={rows.length ? "Change the filter to see the others." : "Register each drawing with its number and revision."} testId="drawings-empty" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table data-testid="drawing-table">
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Title</TableHead><TableHead>Discipline</TableHead><TableHead>Rev</TableHead><TableHead>Status</TableHead><TableHead>Issued</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setOpenId(r.id)} data-testid={`drawing-row-${r.number}`}>
                  <TableCell className="font-mono text-xs font-medium">{r.number}</TableCell><TableCell className="max-w-[22rem] truncate">{r.title}</TableCell>
                  <TableCell>{r.discipline || "—"}</TableCell><TableCell className="font-mono text-xs">{r.revision}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell><TableCell className="text-muted-foreground">{r.issue_date ? fmtDate(r.issue_date) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {adding && <DrawingForm base={base} onClose={() => setAdding(false)} onSaved={(d) => { setAdding(false); load(); setOpenId(d.id); }} />}
      {openId && <DrawingDetail base={base} id={openId} can={{ create: canCreate, edit: canEdit, approve: canApprove }} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function DrawingForm({ base, onClose, onSaved }) {
  const [f, setF] = useState({ number: "", title: "", discipline: "", revision: "R0", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post(base, { ...f, discipline: f.discipline || null, notes: f.notes || null }); toast.success(`${data.number} registered`); onSaved(data); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="drawing-form">
        <DialogHeader><DialogTitle>Register a drawing</DialogTitle><DialogDescription>Each number is registered once. Later changes are new revisions.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Drawing number</Label><Input value={f.number} onChange={set("number")} className="mt-1.5" data-testid="drawing-number" /></div>
          <div><Label>Revision</Label><Input value={f.revision} onChange={set("revision")} maxLength={12} className="mt-1.5" data-testid="drawing-revision" /></div>
          <div className="col-span-2"><Label>Title</Label><Input value={f.title} onChange={set("title")} className="mt-1.5" data-testid="drawing-title" /></div>
          <div className="col-span-2"><Label>Discipline</Label>
            <Select value={f.discipline || "none"} onValueChange={(v) => setF((x) => ({ ...x, discipline: v === "none" ? "" : v }))}>
              <SelectTrigger className="mt-1.5" data-testid="drawing-discipline"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Not set</SelectItem>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2"><Label>Notes (optional)</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={saving || !f.number.trim() || !f.title.trim()} onClick={save} data-testid="drawing-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Register</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DrawingDetail({ base, id, can, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ask, setAsk] = useState(null);                 // null | { kind: "revise" | "back" | "void" }
  const [text, setText] = useState("");
  const [rev, setRev] = useState("");
  const load = useCallback(async () => { try { setD((await api.get(`${base}/${id}`)).data); } catch (err) { toast.error(getErrorMessage(err)); } }, [base, id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const run = async (name, path, body, ok) => {
    setBusy(name);
    try { await api.post(`${base}/${id}/${path}`, body); toast.success(ok); setAsk(null); setText(""); setRev(""); await load(); onChanged(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  const status = (s, ok, note) => run(s, "status", { status: s, note: note || null }, ok);
  const live = !["SUPERSEDED", "VOID"].includes(d.status);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="drawing-detail">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2"><FileStack className="h-5 w-5 text-[#910029]" /> {d.number} <span className="font-mono text-sm">{d.revision}</span> <StatusPill value={d.status} /></DialogTitle>
          <DialogDescription>{d.title}{d.discipline ? ` · ${d.discipline}` : ""}{d.issue_date ? ` · issued ${fmtDate(d.issue_date)}` : ""}</DialogDescription></DialogHeader>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">History</p>
          <ul className="space-y-1 text-sm" data-testid="drawing-history">
            {[...(d.history || [])].reverse().map((h) => (
              <li key={h.seq} className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs">{h.revision}</span><StatusPill value={h.status} />
                <span className="text-xs text-muted-foreground">{h.note || ""}{h.by ? ` · ${h.by}` : ""}{h.at ? ` · ${fmtDate(h.at)}` : ""}</span></li>))}
          </ul>
        </div>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {live && can.create && d.status !== "DRAFT" && <Button variant="outline" onClick={() => setAsk({ kind: "revise" })} data-testid="drawing-revise">New revision</Button>}
            {live && can.approve && <Button variant="ghost" onClick={() => setAsk({ kind: "void" })} data-testid="drawing-void">Void</Button>}
          </div>
          <div className="flex flex-wrap gap-2">
            {d.status === "DRAFT" && can.edit && <Button disabled={!!busy} onClick={() => status("FOR_APPROVAL", `${d.number} sent for approval`)} data-testid="drawing-submit"><Send className="h-4 w-4" /> Send for approval</Button>}
            {d.status === "FOR_APPROVAL" && can.approve && <>
              <Button variant="outline" disabled={!!busy} onClick={() => setAsk({ kind: "back" })} data-testid="drawing-back"><Undo2 className="h-4 w-4" /> Send back</Button>
              <Button disabled={!!busy} onClick={() => status("APPROVED", `${d.number} approved`)} data-testid="drawing-approve"><CheckCircle2 className="h-4 w-4" /> Approve</Button></>}
            {d.status === "APPROVED" && can.approve && <Button disabled={!!busy} onClick={() => status("ISSUED", `${d.number} issued for construction`)} data-testid="drawing-issue">Issue for construction</Button>}
          </div>
        </DialogFooter>
        {ask && (
          <Dialog open onOpenChange={(o) => !o && setAsk(null)}>
            <DialogContent className="max-w-md" data-testid="drawing-ask">
              <DialogHeader><DialogTitle>{ask.kind === "revise" ? `New revision of ${d.number}` : ask.kind === "back" ? "Send back to draft" : `Void ${d.number}`}</DialogTitle></DialogHeader>
              {ask.kind === "revise" && <div><Label>New revision</Label><Input value={rev} onChange={(e) => setRev(e.target.value)} maxLength={12} placeholder="R1" className="mt-1.5" data-testid="drawing-new-revision" /></div>}
              <div><Label>{ask.kind === "revise" ? "What changed (optional)" : "Why"}</Label><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5" data-testid="drawing-ask-text" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setAsk(null)}>Cancel</Button>
                <Button disabled={!!busy || (ask.kind === "revise" ? !rev.trim() : text.trim().length < 5)} data-testid="drawing-ask-submit"
                  onClick={() => (ask.kind === "revise" ? run("revise", "revise", { revision: rev.trim(), note: text.trim() || null }, `${d.number} is now ${rev.trim()}`) : status(ask.kind === "back" ? "DRAFT" : "VOID", ask.kind === "back" ? "Sent back" : `${d.number} voided`, text.trim()))}>Confirm</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
